import { useReducer } from 'react';

import { RequestStatus } from '../../types';
import { Filter } from '../types';

/**
 * Интерфейс состояния автозаполнения.
 *
 * @typeParam T - Тип элементов, используемых в автозаполнении.
 */
interface State<T> {
  status: RequestStatus;
  items: T[];
  hasMore: boolean;
  abortedReqIds: symbol[];
  query: string;
}

/**
 * Перечисление типов действий для управления состоянием автозаполнения.
 */
enum Actions {
  setQuery = 'setQuery',
  setStatus = 'setStatus',
  setItems = 'setItems',
  addItems = 'addItems',
  setError = 'setError',
  setHasMore = 'setHasMore',
  resetState = 'resetState',
  abortRequest = 'abortRequest',
}
type SetQueryAction = {
  type: Actions.setQuery;
  payload: string;
};

type SetStatusAction = {
  type: Actions.setStatus;
  payload: {
    status: RequestStatus;
    reqId: symbol;
  };
};

type SetItemsAction<T> = {
  type: Actions.setItems | Actions.addItems;
  payload: {
    items: T[];
    reqId: symbol;
  };
};

type SetHasMoreAction = {
  type: Actions.setHasMore;
  payload: {
    hasMore: boolean;
    reqId: symbol;
  };
};

type ResetStateAction = {
  type: Actions.resetState;
  payload?: never;
};

type AbortRequestAction = {
  type: Actions.abortRequest;
  payload: symbol;
};

type AutocompleteAction<T> =
  | SetQueryAction
  | SetStatusAction
  | SetHasMoreAction
  | ResetStateAction
  | AbortRequestAction
  | SetItemsAction<T>;

/**
 * Хук useAutocompleteReducer используется для управления состоянием автозаполнения
 * в компонентах ввода.
 *
 * @typeParam T - Тип элементов, используемых в автозаполнении.
 * @param callback - Асинхронная функция, которая будет вызываться для получения элементов автозаполнения на основе переданного фильтра.
 * @param doNotRepeatSimilarRequest - Флаг, предотвращающий повторение одинаковых запросов.
 * @returns Объект с текущим состоянием и функциями для управления им.
 */
export const useAutocompleteReducer = <T>(
  callback?: (filter: Filter) => Promise<T[]>,
  doNotRepeatSimilarRequest = false,
) => {
  const initialState: State<T> = {
    status: 'idle',
    items: [],
    hasMore: true,
    abortedReqIds: [],
    query: '',
  };

  const reducer = (
    state: State<T>,
    { type, payload }: AutocompleteAction<T>,
  ): State<T> => {
    const isActionCancelled = (reqId: symbol) => {
      return state.abortedReqIds.includes(reqId);
    };

    switch (type) {
      case Actions.setQuery:
        return {
          ...state,
          query: payload,
        };

      case Actions.setStatus:
        return isActionCancelled(payload.reqId)
          ? {
              ...state,
            }
          : {
              ...state,
              status: payload.status,
            };

      case Actions.setItems:
        return isActionCancelled(payload.reqId)
          ? { ...state }
          : {
              ...state,
              items: payload.items,
            };

      case Actions.addItems:
        return isActionCancelled(payload.reqId)
          ? { ...state }
          : {
              ...state,
              items: [...state.items, ...payload.items],
            };

      case Actions.setHasMore:
        return isActionCancelled(payload.reqId)
          ? { ...state }
          : {
              ...state,
              hasMore: payload.hasMore,
            };

      case Actions.abortRequest:
        return {
          ...state,
          abortedReqIds: [...state.abortedReqIds, payload],
        };

      case Actions.resetState:
        return initialState;

      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const setStatus = (payload: {
    status: RequestStatus;
    reqId: symbol;
  }): SetStatusAction => ({
    type: Actions.setStatus,
    payload,
  });

  const setQuery = (payload: string): SetQueryAction => ({
    type: Actions.setQuery,
    payload,
  });

  const setItems = (payload: {
    items: T[];
    reqId: symbol;
  }): SetItemsAction<T> => ({
    type: Actions.setItems,
    payload,
  });

  const addItems = (payload: {
    items: T[];
    reqId: symbol;
  }): SetItemsAction<T> => ({
    type: Actions.addItems,
    payload,
  });

  const setHasMore = (payload: {
    hasMore: boolean;
    reqId: symbol;
  }): SetHasMoreAction => ({
    type: Actions.setHasMore,
    payload,
  });

  const abortRequest = (payload: symbol) =>
    dispatch({
      type: Actions.abortRequest,
      payload,
    });

  const resetState = (): ResetStateAction => ({
    type: Actions.resetState,
  });

  const requestItemsInner = async (
    filter: Filter,
    type: 'get' | 'search',
    reqId: symbol,
  ) => {
    if (doNotRepeatSimilarRequest) {
      if (state.query === filter.query && state.status === 'complete') {
        dispatch(setStatus({ status: 'complete', reqId }));
        return state.items;
      }
    }
    try {
      dispatch(setQuery(filter.query));

      const items = (await callback?.(filter)) || [];

      if (type === 'get') {
        dispatch(addItems({ items, reqId }));
      } else {
        dispatch(setItems({ items, reqId }));
      }

      dispatch(setHasMore({ hasMore: items.length >= filter.limit, reqId }));
      dispatch(setStatus({ status: 'complete', reqId }));
    } catch (err) {
      dispatch(setStatus({ status: 'failed', reqId }));
    }
  };

  const requestItems = (filter: Filter, type: 'get' | 'search') => {
    const reqId = Symbol('reqId');

    dispatch(setStatus({ status: 'loading', reqId }));
    requestItemsInner(filter, type, reqId);

    return () => abortRequest(reqId);
  };

  const resetItems = () => dispatch(resetState());

  return {
    ...state,
    requestItems: callback ? requestItems : undefined,
    resetItems,
  };
};
